import React, { useRef } from 'react'
import { loadModules } from 'esri-loader'
import { useTranslation } from 'react-i18next'

import { ClusterConfig } from 'components/Reporting/Overview/GraphicalDetails/ContributionsLocation/ClusterMap/ClusterConfig'
import { FeatureConfig } from 'components/Reporting/Overview/GraphicalDetails/ContributionsLocation/ClusterMap/FeatureConfig'
import { ContributionsLocationData as ContributionsLocationDataType } from 'types/ContributionsLocation'
import variables from 'assets/styles/scss/shared/_variables.module.scss'
import { trackActions } from 'containers/Helper'
import { isFrenchLocale } from 'lib/utility/LocaleHelper'
import './MapBox.scss'

type MapProps = {
  contributionsLocationData: ContributionsLocationDataType
}

const Map = (props: MapProps) => {
  const { contributionsLocationData } = props
  const MapElement = useRef(null)
  const { t } = useTranslation()
  const DEFAULT_ZOOM_LEVEL = 8

  const setMapLocale = (intl: any) => {
    isFrenchLocale() ? intl.setLocale('fr') : intl.setLocale('en-GB')
  }

  const setHomeWidget = (Home: any, view: any) => {
    const homeBtn = new Home({
      view: view,
    })

    view.ui.add(homeBtn, 'top-left')
  }

  const setExtent = (Extent: any, view: any) => {
    if (!contributionsLocationData.bounds) return

    const extent = new Extent(contributionsLocationData.bounds)

    view.extent = extent
  }

  const setHighlightOptions = (view: any) => {
    view.highlightOptions = {
      haloColor: variables['iris-900'],
      haloOpacity: 0.5,
      fillOpacity: 0.01,
    }
  }

  const setHoverBehaviour = (view: any, featureLayer: any) => {
    view.on('pointer-move', (event: any) => {
      view.hitTest(event).then((response: any) => {
        const feature = response.results.filter((result: any) => {
          return result.graphic.layer === featureLayer
        })[0]

        if (feature && feature.graphic) {
          if (view.popup.visible) return

          const graphic = feature.graphic
          view.popup.open({
            location: graphic.geometry.centroid,
            features: [graphic],
          })
        } else {
          view.popup.close()
        }
      })
    })
  }

  const trackMapZoom = (reactiveUtils: any, view: any) => {
    let zoomType = ''
    const zoomButtons = document.querySelectorAll('.esri-view .esri-component')
    zoomButtons[0]?.addEventListener('click', () => {
      zoomType = 'buttons'
    })

    view.on('mouse-wheel', () => {
      zoomType = 'mouse scroll'
    })

    reactiveUtils.watch(
      () => [view.stationary],
      ([stationary]: any) => {
        if (stationary && zoomType) {
          trackActions('Map Zoomed', {
            featureName: 'Overview Dashboard',
            sectionName: 'Map',
            zoomType,
          })
          zoomType = ''
        }
      },
    )
  }

  const trackMarker = (isCluster: boolean) => {
    trackActions('Marker Clicked', {
      featureName: 'Overview Dashboard',
      sectionName: 'Widget',
      markerType: isCluster ? 'Cluster' : 'Marker',
      widgetType: 'Map',
      widgetName: 'Contribution locations',
    })
  }

  const setTrackEvent = (view: any, featureLayer: any) => {
    view.on('click', (event: any) => {
      view.hitTest(event).then((response: any) => {
        const feature = response.results.filter((result: any) => {
          return result.graphic.layer === featureLayer
        })[0]
        const isCluster = response.results[0].graphic.isAggregate
        if (feature && feature.graphic) {
          trackMarker(isCluster)
        }
      })
    })
  }

  const setPopup = (view: any) => {
    const popup = view.popup
    popup.collapseEnabled = false
    popup.visibleElements = {
      closeButton: false,
      featureNavigation: false,
    }
    popup.viewModel.includeDefaultActions = false
  }

  //Logic to generate the map pointers with the coordinate and attributes
  const createMapGraphic = (
    point: any,
    Graphic: any,
    featureCoordinate: any,
  ) => {
    return new Graphic({
      geometry: featureCoordinate,
      attributes: {
        id: point.id,
        contributions: point.contributions,
        contributors: point.contributors,
        name: point.name,
      },
    })
  }

  const getLocationData = (Point: any, Graphic: any, SpatialReference: any) => {
    if (!contributionsLocationData.maps) return []

    const featurePoints = contributionsLocationData.maps.reduce(
      (acc: any[], point: any) => {
        // Skip the invalid points
        const [x, y] = point.coordinates
        if (x === 0 && y === 0 && point.wkid === 0) {
          return acc // Continue to the next iteration without adding to the result
        }

        // Create the feature point
        const featureCoordinate = new Point({
          x: point.coordinates[0],
          y: point.coordinates[1],
          spatialReference: new SpatialReference({
            wkid: point.wkid,
          }),
        })

        // Add the valid graphic to the accumulator
        acc.push(createMapGraphic(point, Graphic, featureCoordinate))

        return acc
      },
      [],
    )

    return featurePoints
  }

  const loadMap = (
    Map: any,
    FeatureLayer: any,
    MapView: any,
    Graphic: any,
    Point: any,
    SpatialReference: any,
    Home: any,
    Extent: any,
    reactiveUtils: any,
    intl: any,
    Basemap: any,
  ) => {
    const locale = intl.getLocale()
    const layer = new FeatureLayer({
      source: getLocationData(Point, Graphic, SpatialReference),
      featureReduction: ClusterConfig(t),
      ...FeatureConfig(t),
    })

    const basemap = new Basemap({
      portalItem: {
        id: '4ee49e2bbc0644b19fb96fef466f0de4',
      },
    })

    const map = new Map({
      layers: [layer],
      basemap: locale === 'fr' ? basemap : 'arcgis-topographic',
    })

    const view = new MapView({
      container: MapElement.current,
      center: [0, 0],
      zoom: 3,
      wrapAround: false,
      constraints: {
        snapToZoom: false,
        rotationEnabled: false,
        minZoom: 3,
        maxZoom: 8,
        geometry: new Extent({
          xmin: -180,
          ymin: -60,
          xmax: 180,
          ymax: 75,
        }),
      },
      map,
    })

    //Return if there are no contributions to display
    if (!contributionsLocationData.maps) return []

    // Focus on the location with maximum contributions, skipping [0, 0]
    const validPoints = contributionsLocationData.maps.filter(
      (point: any) =>
        JSON.stringify(point.coordinates) !== JSON.stringify([0, 0]) &&
        point.wkid !== 0,
    )

    if (validPoints.length) {
      const maxContributionPoint = validPoints.reduce(
        (max: any, point: any) =>
          point.contributions > (max?.contributions || 0) ? point : max,
        null,
      )

      if (maxContributionPoint) {
        const targetPoint = new Point({
          x: maxContributionPoint.coordinates[0],
          y: maxContributionPoint.coordinates[1],
          spatialReference: { wkid: maxContributionPoint.wkid },
        })

        view.center = [targetPoint.x, targetPoint.y] // Set the initial center to the valid point
        view.zoom = DEFAULT_ZOOM_LEVEL // The zoom level can be adjusted with this value
      }
    }

    setHomeWidget(Home, view)
    setExtent(Extent, view)
    setPopup(view)
    setHoverBehaviour(view, layer)
    setHighlightOptions(view)
    setTrackEvent(view, layer)
    trackMapZoom(reactiveUtils, view)
    setMapLocale(intl)
  }

  loadModules(
    [
      'esri/config',
      'esri/Map',
      'esri/layers/FeatureLayer',
      'esri/views/MapView',
      'esri/Graphic',
      'esri/geometry/Point',
      'esri/geometry/SpatialReference',
      'esri/widgets/Home',
      'esri/geometry/Extent',
      'esri/core/reactiveUtils',
      'esri/intl',
      'esri/Basemap',
      'esri/geometry/Extent',
    ],
    {
      css: true,
    },
  ).then(
    ([
      esriConfig,
      Map,
      FeatureLayer,
      MapView,
      Graphic,
      Point,
      SpatialReference,
      Home,
      Extent,
      reactiveUtils,
      intl,
      Basemap,
    ]) => {
      esriConfig.apiKey = process.env.REACT_APP_ESRI_ACCESS_TOKEN as string
      loadMap(
        Map,
        FeatureLayer,
        MapView,
        Graphic,
        Point,
        SpatialReference,
        Home,
        Extent,
        reactiveUtils,
        intl,
        Basemap,
      )
    },
  )

  return <div style={{ height: '31.5rem' }} ref={MapElement}></div>
}
export default Map
